import { useMemo } from 'react'
import { DataTable } from '@shared/components/ui/data-table'
import { StatusRegistry } from '@shared/lib/filters/status-registry'
import { applyColumnManagement, type ColumnManagementConfig } from '@shared/lib/tables/utils/column-management'
import type { Column, TableAction, TableActions, SortState } from '@shared/types/data-table'
import type { EmptyStateAction, EntityType } from '@shared/components/ui/data-table/EmptyState'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface BaseEntityTableProps<T extends Record<string, any>> {
  // Core data props
  data: T[]
  selectedItems: string[]
  onSelectionChange: (selectedIds: string[]) => void
  onRowClick: (item: T) => void
  getItemId: (item: T) => string
  getItemStatus?: (item: T) => string

  // Pagination
  pagination: {
    pageSize: number
    currentPage: number
    onPageChange: (page: number) => void
    totalItems: number
    totalPages: number
  }

  // UI state
  loading: boolean
  activeItemId?: string

  // Table configuration
  entityType: EntityType
  statusFilter?: string
  columns?: Column<T>[] | Record<string, Column<T>>
  getColumns?: (statusFilter: string) => Column<T>[]
  managedColumns?: ColumnManagementConfig[]

  // Actions
  rowActions?: TableActions<T> | ((item: T) => TableActions<T>)
  hoverActions?: TableAction<T>[] | ((item: T) => TableAction<T>[])

  // Empty state
  emptyStateActions?: EmptyStateAction[]

  // Sorting
  sortState?: SortState
  onSortChange?: (sortState: SortState) => void

  // Selection constraints
  unavailableItems?: string[]
  allowedStatuses?: string[]
  onSelectSucceededOnly?: (fn: () => void) => void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function BaseEntityTable<T extends Record<string, any>>({
  data,
  selectedItems,
  onSelectionChange,
  onRowClick,
  getItemId,
  getItemStatus,
  pagination,
  loading,
  activeItemId,
  entityType,
  statusFilter,
  columns,
  getColumns,
  managedColumns,
  rowActions,
  hoverActions,
  emptyStateActions,
  sortState: _sortState,
  onSortChange: _onSortChange,
  unavailableItems = [],
  allowedStatuses,
  onSelectSucceededOnly,
}: BaseEntityTableProps<T>) {

  const resolvedColumns = useMemo(() => {
    let baseColumns: Column<T>[]

    if (getColumns) {
      baseColumns = getColumns(statusFilter || 'all')
    } else if (Array.isArray(columns)) {
      baseColumns = columns
    } else if (columns) {
      baseColumns = Object.values(columns)
    } else {
      throw new Error('Either columns or getColumns must be provided')
    }

    if (managedColumns) {
      return applyColumnManagement(baseColumns, managedColumns)
    }

    return baseColumns
  }, [columns, getColumns, statusFilter, managedColumns])

  const resolvedAllowedStatuses = useMemo(() => {
    if (allowedStatuses) return allowedStatuses

    // Try to get default allowed statuses from registry
    if (StatusRegistry.hasEntity(entityType)) {
      const mappings = StatusRegistry.getMappings(entityType)
      if (mappings.succeeded) {
        return [...mappings.succeeded]
      }
      if (mappings.active) {
        return [...mappings.active]
      }
    }

    return []
  }, [allowedStatuses, entityType])

  return (
    <DataTable<T>
      data={data}
      columns={resolvedColumns}
      selectedItems={selectedItems}
      onSelectionChange={onSelectionChange}
      getItemId={getItemId}
      onRowClick={onRowClick}
      rowActions={rowActions}
      hoverActions={hoverActions}
      pagination={pagination}
      loading={loading}
      emptyStateEntityType={entityType}
      emptyStateActions={emptyStateActions}
      getItemStatus={getItemStatus}
      allowedStatuses={resolvedAllowedStatuses}
      unavailableItems={unavailableItems}
      onSelectSucceededOnly={onSelectSucceededOnly}
      activeItemId={activeItemId}
    />
  )
}
